import { type ContentLang } from '@yoyoclub/shared';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

import { BilingualField } from '../../components/BilingualField';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  bilingualPayload,
  hasBilingualValue,
  toBilingualValue,
  type BilingualValue,
} from '../../components/bilingualValue';
import { useAuth } from '../../hooks/useAuth';
import { localize, toScript } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

interface HostActivity {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  status: string;
  slug: string;
}

interface HostPass {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  status: string;
  slug: string;
  expiresAt: string;
}

interface Host {
  id: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  bioZh: string | null;
  bioEn: string | null;
  bioAutoLang: ContentLang | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  user: {
    email: string;
  };
  activities: HostActivity[];
  passes: HostPass[];
  /** How many users follow this host. Count only, never identities. */
  followerCount: number;
}

const statusConfig = {
  PENDING: { label: 'Pending', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  APPROVED: { label: 'Approved', bg: 'bg-green-100', text: 'text-green-800' },
  REJECTED: { label: 'Rejected', bg: 'bg-red-100', text: 'text-red-800' },
};

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function BioPopover({
  hostId,
  firstName,
  lastName,
  bioEn,
  bioZh,
  bioAutoLang,
  onSaved,
  getToken,
}: {
  hostId: string;
  firstName: string | null;
  lastName: string | null;
  bioEn: string | null;
  bioZh: string | null;
  bioAutoLang: ContentLang | null;
  onSaved: (hostId: string, bio: BilingualValue) => void;
  getToken: () => Promise<string | undefined>;
}) {
  const siteLanguage = useSiteLanguage();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const savedBio = (): BilingualValue =>
    toBilingualValue({ zh: bioZh, en: bioEn, autoLang: bioAutoLang });
  const [edit, setEdit] = useState<BilingualValue>(savedBio);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setEditing(false);
        setSaveError(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      if (!hasBilingualValue(edit)) throw new Error('Enter a bio in Chinese or English');
      const res = await fetch(`${API_URL}/host/profile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostId, ...bilingualPayload(edit, 'bio') }),
      });
      if (!res.ok) throw new Error('Failed to save');
      onSaved(hostId, edit);
      setEditing(false);
    } catch (err) {
      setSaveError(
        err instanceof Error && err.message.startsWith('Enter') ? err.message : '保存失败，请重试'
      );
    } finally {
      setSaving(false);
    }
  };

  const hasBio = bioEn || bioZh;

  return (
    <div ref={ref} className="relative inline-block mt-1">
      <button onClick={() => setOpen((v) => !v)} className="text-sm text-blue-600 hover:underline">
        Bio
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 w-80 bg-white border border-stone-200 rounded-lg shadow-lg p-3">
          {editing ? (
            <div className="space-y-2">
              <BilingualField
                compact
                stacked
                fieldType="hostBio"
                label="Bio"
                multiline
                rows={3}
                value={edit}
                onChange={setEdit}
                sideLabels={{ EN: 'EN', ZH: '中文' }}
                labelClassName="text-xs font-medium text-stone-400 block mb-0.5"
                inputClassName="w-full text-xs border border-stone-200 rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-stone-400"
              />
              {saveError && <p className="text-xs text-red-500">{saveError}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => {
                    setEditing(false);
                    setEdit(savedBio());
                    setSaveError(null);
                  }}
                  className="text-xs text-stone-500 hover:text-stone-700 px-2 py-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="text-xs bg-stone-800 text-white rounded px-3 py-1 hover:bg-stone-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <p className="text-xs font-medium text-stone-400 mb-0.5">Legal Name</p>
                {firstName || lastName ? (
                  <p className="text-xs text-stone-700 leading-relaxed">
                    {[firstName, lastName].filter(Boolean).join(' ')}
                  </p>
                ) : (
                  <p className="text-xs text-amber-600 italic">Missing</p>
                )}
              </div>
              {hasBio ? (
                <>
                  {bioEn && (
                    <div>
                      <p className="text-xs font-medium text-stone-400 mb-0.5">
                        EN{bioAutoLang === 'EN' ? ' · Auto-translated' : ''}
                      </p>
                      <p className="text-xs text-stone-700 leading-relaxed">{bioEn}</p>
                    </div>
                  )}
                  {bioZh && (
                    <div>
                      <p className="text-xs font-medium text-stone-400 mb-0.5">
                        中文{bioAutoLang === 'ZH' ? ' · Auto-translated' : ''}
                      </p>
                      <p className="text-xs text-stone-700 leading-relaxed">
                        {toScript(bioZh, siteLanguage)}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-stone-400">No bio yet.</p>
              )}
              <div className="flex justify-end pt-1">
                <button
                  onClick={() => {
                    setEditing(true);
                    setEdit(savedBio());
                  }}
                  className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a4 4 0 01-1.414.828l-3 1 1-3a4 4 0 01.828-1.414z"
                    />
                  </svg>
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminHosts() {
  const siteLanguage = useSiteLanguage();
  const { getToken } = useAuth();

  const [hosts, setHosts] = useState<Host[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  // Per-host collapsed state for archived activities and expired passes
  const [showArchivedActivities, setShowArchivedActivities] = useState<Record<string, boolean>>({});
  const [showExpiredPasses, setShowExpiredPasses] = useState<Record<string, boolean>>({});

  const fetchHosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();

      const res = await fetch(`${API_URL}/admin/hosts`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch hosts.');
      }

      const result = await res.json();
      setHosts(result.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hosts.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchHosts();
  }, [fetchHosts]);

  const handleApprove = async (hostId: string) => {
    try {
      setApprovingId(hostId);
      setError(null);
      const token = await getToken();

      const res = await fetch(`${API_URL}/admin/hosts/${hostId}/approve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to approve host.');
      }

      setHosts((prev) =>
        prev.map((h) => (h.id === hostId ? { ...h, status: 'APPROVED' as const } : h))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed.');
    } finally {
      setApprovingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const now = new Date();

  return (
    <AdminLayout title="Host Management">
      <div className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-4 text-red-500 hover:text-red-700">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-stone-200 border-t-amber-700 rounded-full animate-spin" />
          </div>
        ) : hosts.length === 0 ? (
          <div className="text-center py-20 text-stone-500">No host applications yet.</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <colgroup>
                  <col className="w-48" />
                  <col className="w-44" />
                  <col className="w-24" />
                  <col />
                  <col />
                  <col className="w-24" />
                  <col className="w-28" />
                  <col className="w-20" />
                </colgroup>
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-200">
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Activities
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Passes
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Followers
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Applied Date
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {hosts.map((host) => {
                    const status = statusConfig[host.status];

                    const activeActivities = host.activities.filter((a) => a.status !== 'INACTIVE');
                    const archivedActivities = host.activities.filter(
                      (a) => a.status === 'INACTIVE'
                    );
                    const activePasses = host.passes.filter((p) => new Date(p.expiresAt) >= now);
                    const expiredPasses = host.passes.filter((p) => new Date(p.expiresAt) < now);

                    const showArchived = showArchivedActivities[host.id] ?? false;
                    const showExpired = showExpiredPasses[host.id] ?? false;

                    return (
                      <tr key={host.id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-start gap-2.5">
                            {host.avatarUrl ? (
                              <img
                                src={host.avatarUrl}
                                alt={host.displayName}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-sm font-medium text-stone-500">
                                  {host.displayName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-stone-800 break-words">
                                {host.displayName}
                              </div>
                              <BioPopover
                                hostId={host.id}
                                firstName={host.firstName}
                                lastName={host.lastName}
                                bioEn={host.bioEn}
                                bioZh={host.bioZh}
                                bioAutoLang={host.bioAutoLang}
                                getToken={getToken}
                                onSaved={(id, bio) =>
                                  setHosts((prev) =>
                                    prev.map((h) =>
                                      h.id === id
                                        ? {
                                            ...h,
                                            bioZh: bio.zh.trim() || null,
                                            bioEn: bio.en.trim() || null,
                                            bioAutoLang: bio.autoLang,
                                          }
                                        : h
                                    )
                                  )
                                }
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-stone-600 break-all">
                          {host.user.email}
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}
                          >
                            {status.label}
                          </span>
                        </td>

                        {/* Activities column */}
                        <td className="px-4 py-4">
                          {host.activities.length === 0 ? (
                            <span className="text-sm text-stone-400">—</span>
                          ) : (
                            <div className="space-y-1">
                              {activeActivities.map((activity) => {
                                const isDraft = activity.status === 'DRAFT';
                                return (
                                  <Link
                                    key={activity.id}
                                    to={`/admin/activities/${activity.id}/edit`}
                                    className={`block text-sm hover:underline line-clamp-2 ${
                                      isDraft ? 'text-amber-700 font-medium' : 'text-stone-600'
                                    }`}
                                  >
                                    {isDraft && (
                                      <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1.5 relative -top-px" />
                                    )}
                                    {localize(activity.titleCN, activity.titleEN, siteLanguage)}
                                    {isDraft && (
                                      <span className="ml-1.5 text-xs text-amber-600">
                                        (Pending)
                                      </span>
                                    )}
                                  </Link>
                                );
                              })}
                              {archivedActivities.length > 0 && (
                                <div>
                                  <button
                                    onClick={() =>
                                      setShowArchivedActivities((prev) => ({
                                        ...prev,
                                        [host.id]: !showArchived,
                                      }))
                                    }
                                    className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-500 py-0.5"
                                  >
                                    <ChevronIcon expanded={showArchived} />
                                    {archivedActivities.length} archived
                                  </button>
                                  {showArchived && (
                                    <div className="space-y-1 mt-1">
                                      {archivedActivities.map((activity) => (
                                        <Link
                                          key={activity.id}
                                          to={`/admin/activities/${activity.id}/edit`}
                                          className="block text-sm text-stone-400 hover:underline"
                                        >
                                          {localize(
                                            activity.titleCN,
                                            activity.titleEN,
                                            siteLanguage
                                          )}
                                        </Link>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Passes column */}
                        <td className="px-4 py-4">
                          {host.passes.length === 0 ? (
                            <span className="text-sm text-stone-400">—</span>
                          ) : (
                            <div className="space-y-1">
                              {activePasses.map((pass) => {
                                const isPending = pass.status === 'PENDING_APPROVAL';
                                const isPaused = pass.status === 'PAUSED';
                                return (
                                  <Link
                                    key={pass.id}
                                    to={`/admin/passes/${pass.id}/edit`}
                                    className={`block text-sm hover:underline line-clamp-2 ${
                                      isPending
                                        ? 'text-amber-700 font-medium'
                                        : isPaused
                                          ? 'text-stone-400'
                                          : 'text-stone-600'
                                    }`}
                                  >
                                    {isPending && (
                                      <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1.5 relative -top-px" />
                                    )}
                                    {localize(pass.titleCN, pass.titleEN, siteLanguage)}
                                    {isPending && (
                                      <span className="ml-1.5 text-xs text-amber-600">
                                        (Pending)
                                      </span>
                                    )}
                                    {isPaused && (
                                      <span className="ml-1.5 text-xs text-stone-400">
                                        (Paused)
                                      </span>
                                    )}
                                  </Link>
                                );
                              })}
                              {expiredPasses.length > 0 && (
                                <div>
                                  <button
                                    onClick={() =>
                                      setShowExpiredPasses((prev) => ({
                                        ...prev,
                                        [host.id]: !showExpired,
                                      }))
                                    }
                                    className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-500 py-0.5"
                                  >
                                    <ChevronIcon expanded={showExpired} />
                                    {expiredPasses.length} expired
                                  </button>
                                  {showExpired && (
                                    <div className="space-y-1 mt-1">
                                      {expiredPasses.map((pass) => (
                                        <Link
                                          key={pass.id}
                                          to={`/admin/passes/${pass.id}/edit`}
                                          className="block text-sm text-stone-400 hover:underline"
                                        >
                                          {localize(pass.titleCN, pass.titleEN, siteLanguage)}
                                        </Link>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Count only: who follows a host is between them and
                            the user, so no identities are surfaced here. */}
                        <td className="px-4 py-4 text-sm text-stone-600">
                          {host.followerCount > 0 ? (
                            host.followerCount
                          ) : (
                            <span className="text-stone-400">—</span>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-stone-600">
                          {formatDate(host.createdAt)}
                        </td>
                        <td className="px-4 py-4">
                          {host.status === 'PENDING' && (
                            <button
                              onClick={() => handleApprove(host.id)}
                              disabled={approvingId === host.id}
                              className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                              {approvingId === host.id ? 'Approving...' : 'Approve'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
